import { useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { InputSwitch } from 'primereact/inputswitch'
import { confirmDialog } from 'primereact/confirmdialog'
import { Controller, useFormContext } from 'react-hook-form'
import { z } from 'zod'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import DurationInput from '../../../components/admin/DurationInput.js'
import { ZodForm, FormText, FormDropdown } from '../../../components/forms/index.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'
import { formatDuration } from '../../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Types — derived from apps/api/src/routes/conversations/admin/sla-policies.ts
// + packages/conversations/src/schema/tenant-tables.ts (sla_policies table)
// ---------------------------------------------------------------------------

export interface SlaPolicy {
  id: string
  name: string
  firstResponseMinutes: number
  resolutionMinutes: number
  businessHoursConfigId: string | null
  appliesToConditions: Record<string, unknown>
  sortOrder: number
  escalationAgentId: string | null
  escalationQueueId: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface BhConfig {
  id: string
  name: string
}

// Stable module-level constant — avoids zustand/selector fresh-ref pitfall and
// re-render churn from inline `?? []`.
const EMPTY_BH: BhConfig[] = []

const SlaSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  firstResponseMinutes: z.number().int().positive('First response must be positive'),
  resolutionMinutes: z.number().int().positive('Resolution must be positive'),
  businessHoursConfigId: z.string().optional().or(z.literal('')),
  isActive: z.boolean(),
})

type SlaFormValues = z.infer<typeof SlaSchema>

// ---------------------------------------------------------------------------
// Duration field wrapped for react-hook-form (keeps the ZodForm pattern).
// ---------------------------------------------------------------------------

function FormDuration({ name, label }: { name: string; label: string }): JSX.Element {
  const { control } = useFormContext()
  return (
    <div className="mb-3 flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <DurationInput
            value={typeof field.value === 'number' ? field.value : 0}
            onChange={field.onChange}
            min={1}
          />
        )}
      />
    </div>
  )
}

export default function SlaPoliciesPage(): JSX.Element {
  const { items, loading, create, update, remove } = useAdminResource<SlaPolicy>(
    '/admin/conversations/sla-policies',
    { responseKey: 'rows' },
  )
  const { items: bhConfigsRaw } = useAdminResource<BhConfig>(
    '/admin/conversations/business-hours',
    { responseKey: 'rows' },
  )
  const bhConfigs = bhConfigsRaw.length > 0 ? bhConfigsRaw : EMPTY_BH

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<SlaPolicy | null>(null)
  const [copySource, setCopySource] = useState<SlaPolicy | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  function openCreate(): void {
    setEditing(null)
    setCopySource(null)
    setDrawerOpen(true)
  }

  function openEdit(row: SlaPolicy): void {
    setEditing(row)
    setCopySource(null)
    setDrawerOpen(true)
  }

  function openCopy(row: SlaPolicy): void {
    setEditing(null)
    setCopySource(row)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
    setCopySource(null)
  }

  async function handleSubmit(data: SlaFormValues): Promise<void> {
    const payload = {
      name: data.name.trim(),
      firstResponseMinutes: data.firstResponseMinutes,
      resolutionMinutes: data.resolutionMinutes,
      businessHoursConfigId: data.businessHoursConfigId ? data.businessHoursConfigId : undefined,
      isActive: data.isActive,
    }
    if (editing) await update(editing.id, payload)
    else await create(payload)
    closeDrawer()
  }

  function handleDelete(row: SlaPolicy): void {
    confirmDialog({
      message: `Permanently delete "${row.name}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  const columns: ColumnDef<SlaPolicy>[] = [
    { label: 'Name', accessor: 'name' },
    { label: 'First Response', accessor: (r) => formatDuration(r.firstResponseMinutes * 60_000) },
    { label: 'Resolution', accessor: (r) => formatDuration(r.resolutionMinutes * 60_000) },
    {
      label: 'Business Hours',
      accessor: (r) =>
        bhConfigs.find((c) => c.id === r.businessHoursConfigId)?.name ?? '—',
    },
    {
      label: 'Active',
      accessor: (r) => (
        <InputSwitch
          checked={r.isActive}
          onChange={(e) => void update(r.id, { isActive: Boolean(e.value) })}
        />
      ),
    },
  ]

  const defaultValues: SlaFormValues = editing
    ? {
        name: editing.name,
        firstResponseMinutes: editing.firstResponseMinutes,
        resolutionMinutes: editing.resolutionMinutes,
        businessHoursConfigId: editing.businessHoursConfigId ?? '',
        isActive: editing.isActive,
      }
    : copySource
      ? {
          name: `Copy of ${copySource.name}`,
          firstResponseMinutes: copySource.firstResponseMinutes,
          resolutionMinutes: copySource.resolutionMinutes,
          businessHoursConfigId: copySource.businessHoursConfigId ?? '',
          isActive: copySource.isActive,
        }
      : {
          name: '',
          firstResponseMinutes: 30,
          resolutionMinutes: 240,
          businessHoursConfigId: '',
          isActive: true,
        }

  const bhOptions = [
    { label: '— None —', value: '' },
    ...bhConfigs.map((c) => ({ label: c.name, value: c.id })),
  ]

  return (
    <div className="p-4">
      <PageHeader
        title="SLA Policies"
        description="Configure first-response and resolution timers."
        actions={<Button label="+ New SLA Policy" onClick={openCreate} />}
      />
      <ResourceTable<SlaPolicy>
        columns={columns}
        rows={items}
        loading={loading}
        actions={['edit', 'copy', 'delete']}
        onEdit={openEdit}
        onCopy={openCopy}
        onDelete={handleDelete}
        empty={{
          title: 'No SLA policies',
          description: 'Create a policy to track response and resolution timers.',
          action: <Button label="+ New SLA Policy" onClick={openCreate} />,
        }}
      />
      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        title={editing ? `Edit SLA Policy: ${editing.name}` : copySource ? `Copy SLA Policy: ${copySource.name}` : 'New SLA Policy'}
      >
        <ZodForm schema={SlaSchema} defaultValues={defaultValues} onSubmit={handleSubmit}>
          <FormText name="name" label="Name" required autoFocus />
          <FormDuration name="firstResponseMinutes" label="First Response" />
          <FormDuration name="resolutionMinutes" label="Resolution" />
          <FormDropdown name="businessHoursConfigId" label="Business Hours" options={bhOptions} />
          <button type="submit" className="rounded bg-[#16273F] px-4 py-2 text-white">
            Save
          </button>
        </ZodForm>
      </ResourceDrawer>
    </div>
  )
}
